/**
 * Universal logging utility that works in browser, Node.js, and AWS Lambda
 *
 * ## Behavior
 * - **Browser**: Logs with [LEVEL] prefix for console filtering
 * - **Node.js (local)**: Logs with timestamp and level for debugging
 * - **AWS Lambda**: Logs just the message (CloudWatch adds timestamp, request ID, and level)
 *
 * ## Configuration
 *
 * ### Browser (Vite)
 * Set `VITE_LOG_LEVEL` in `.env`:
 * ```
 * VITE_LOG_LEVEL=DEBUG
 * ```
 *
 * ### Node.js / Lambda
 * Set `LOG_LEVEL` environment variable:
 * ```
 * LOG_LEVEL=INFO
 * ```
 *
 * ### Log Levels
 * - **DEBUG**: Show all logs (debug, info, warn, error) - verbose, for development
 * - **INFO**: Show info, warn, error (default) - normal production logging
 * - **WARN**: Show warn, error only - production with reduced noise
 * - **ERROR**: Show error only - production, critical issues only
 * - **NONE**: Disable all logging - silent mode
 *
 * ## Usage
 *
 * ```typescript
 * import { logger } from '@masodj/shared-types';
 *
 * logger.debug('Detailed debug info', { data });
 * logger.info('General information', { status });
 * logger.warn('Warning message', { issue });
 * logger.error('Error occurred', { error });
 * ```
 */

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'NONE';

const LOG_LEVELS: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4,
};

type Environment = 'browser' | 'node' | 'lambda';

class Logger {
  private currentLevel: LogLevel;
  private environment: Environment;

  constructor() {
    // Detect environment
    this.environment = this.detectEnvironment();

    // Read log level from appropriate environment variable
    const envLevel = this.getLogLevelFromEnv();
    this.currentLevel = envLevel && envLevel in LOG_LEVELS ? envLevel : 'INFO';
  }

  private detectEnvironment(): Environment {
    // Check if running in AWS Lambda
    if (typeof process !== 'undefined' && process.env) {
      if (
        process.env.AWS_LAMBDA_FUNCTION_NAME ||
        process.env.AWS_EXECUTION_ENV ||
        process.env.LAMBDA_TASK_ROOT
      ) {
        return 'lambda';
      }
      // Running in Node.js (but not Lambda)
      return 'node';
    }
    // Running in browser
    return 'browser';
  }

  private getLogLevelFromEnv(): LogLevel | undefined {
    if (this.environment === 'browser') {
      // Browser: Check Vite's import.meta.env
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const viteEnv = (import.meta as any).env;
        if (viteEnv && viteEnv.VITE_LOG_LEVEL) {
          return viteEnv.VITE_LOG_LEVEL as LogLevel;
        }
      } catch {
        // import.meta not available (shouldn't happen in browser, but safe fallback)
      }
    } else {
      // Node.js or Lambda: Check process.env
      return process.env.LOG_LEVEL as LogLevel | undefined;
    }
    return undefined;
  }

  private shouldLog(level: LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.currentLevel];
  }

  private formatMessage(level: string, message: string): string {
    switch (this.environment) {
      case 'lambda':
        // Lambda: CloudWatch adds timestamp, request ID, and level - just return message
        return message;

      case 'node': {
        // Node.js: Add timestamp and level for debugging
        const timestamp = new Date().toISOString();
        return `${timestamp} ${level} ${message}`;
      }

      case 'browser':
        // Browser: Add level prefix for console filtering
        return `[${level}] ${message}`;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  debug(message: string, ...args: any[]): void {
    if (this.shouldLog('DEBUG')) {
      console.log(this.formatMessage('DEBUG', message), ...args);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  info(message: string, ...args: any[]): void {
    if (this.shouldLog('INFO')) {
      console.log(this.formatMessage('INFO', message), ...args);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  warn(message: string, ...args: any[]): void {
    if (this.shouldLog('WARN')) {
      console.warn(this.formatMessage('WARN', message), ...args);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error(message: string, ...args: any[]): void {
    if (this.shouldLog('ERROR')) {
      console.error(this.formatMessage('ERROR', message), ...args);
    }
  }

  /**
   * Set log level at runtime (useful for debugging)
   */
  setLevel(level: LogLevel): void {
    this.currentLevel = level;
  }

  /**
   * Get current log level
   */
  getLevel(): LogLevel {
    return this.currentLevel;
  }

  /**
   * Get current environment
   */
  getEnvironment(): Environment {
    return this.environment;
  }

  /**
   * Check if running in Lambda environment
   */
  isLambdaEnvironment(): boolean {
    return this.environment === 'lambda';
  }
}

// Export singleton instance
export const logger = new Logger();

// For convenience, also export individual methods
export const { debug, info, warn, error } = logger;
